// src/admin/pages/AdminMessageView.js
import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaPaperPlane, FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminMessageThread,
  adminReplyToMessage,
  adminUpdateMessageStatus,
} from "../../services/adminMessages.api";

function AdminMessageView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const TEXT = {
    EN: {
      back: "Back",
      ticket: "Conversation",
      from: "From",
      created: "Created",
      replyPlaceholder: "Type your reply...",
      send: "Send",
      close: "Close",
      loading: "Loading conversation...",
emptyMessages: "No messages yet",
    },
    AR: {
      back: "رجوع",
      ticket: "المحادثة",
      from: "من",
      created: "تاريخ الإنشاء",
      replyPlaceholder: "اكتب ردك...",
      send: "إرسال",
      close: "إغلاق",
      loading: "جاري تحميل المحادثة...",
emptyMessages: "لا توجد رسائل بعد",
    },
  }[lang];

  const [isOpen, setIsOpen] = useState(false);
  const [ticket, setTicket] = useState(null);
const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");

  const bottomRef = useRef();

  useEffect(() => {
    async function load() {
  setLoading(true);
  try {
    const thread = await getAdminMessageThread(id);
      setTicket({
        id: thread.id,
        name: thread.full_name,
        subject: thread.subject,
        status: thread.status,
        createdAt: new Date(thread.created_at).toLocaleDateString(),
        phone: thread.phone,
        email: thread.email,
      });
      setMessages(thread.replies || []);
} finally {
  setLoading(false);
}
    }
    load();
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendReply = async () => {
    if (!reply.trim()) return;

    const body = reply;
    setReply("");

    setMessages((prev) => [
      ...prev,
      {
        sender_type: "staff",
        body,
        created_at: new Date().toISOString(),
      },
    ]);

    await adminReplyToMessage(id, body);
  };

  if (loading) {
  return (
    <div className="flex bg-[#f1f1f1] min-h-screen">
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />
      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6">
          {/* SKELETON */}
          <div className="animate-pulse space-y-4">

            <div className="h-6 w-32 bg-gray-200 rounded" />

            <div className="bg-white p-5 rounded-[14px] space-y-3">
              <div className="h-4 w-40 bg-gray-200 rounded" />
              <div className="h-3 w-60 bg-gray-200 rounded" />
              <div className="h-3 w-32 bg-gray-200 rounded" />
            </div>

            <div className="bg-white rounded-[14px] p-4 h-[60vh] space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex justify-start">
                  <div className="h-10 w-40 bg-gray-200 rounded-lg" />
                </div>
              ))}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6">

          {/* BACK */}
          <button
  onClick={() => navigate("/admin/messages")}
  className={`flex items-center gap-2 text-gray-600 text-sm mb-4 hover:text-gray-900 ${
    lang === "AR" ? "flex-row-reverse" : ""
  }`}
>
  <FaArrowLeft className={lang === "AR" ? "rotate-180" : ""} />
  {TEXT.back}
</button>

          {/* HEADER CARD */}
          <div className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-sm p-5 mb-5">
            <h1 className="text-[18px] sm:text-[20px] font-semibold text-gray-900 mb-2">
              {TEXT.ticket} #{ticket.id}
            </h1>

            <p className="text-sm text-gray-900 mb-2">
              {ticket.subject}
            </p>

            <div className="text-sm text-gray-600 space-y-1">
              <p>
                {TEXT.from}: <span className="font-medium text-gray-900">{ticket.name}</span>
              </p>

              {(ticket.phone || ticket.email) && (
                <div className="flex flex-wrap gap-4 text-gray-600">
                  {ticket.phone && <span>📞 {ticket.phone}</span>}
                  {ticket.email && <span className="truncate">✉️ {ticket.email}</span>}
                </div>
              )}

              <p className="text-xs text-gray-400">
                {TEXT.created}: {ticket.createdAt}
              </p>
            </div>
          </div>

          {/* CHAT */}
          <div className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-sm p-4 h-[60vh] overflow-y-auto mb-4">

           {messages.length === 0 ? (
  <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
    <FaPaperPlane className="opacity-30 text-xl" />
    <span className="text-sm">{TEXT.emptyMessages}</span>
  </div>
) : (
   messages.map((m, i) => (
    <div
      key={i}
      className={`flex mb-4 ${
        m.sender_type === "staff" ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`max-w-[80%] px-3 py-2 rounded-[10px] text-[13px] leading-relaxed ${
          m.sender_type === "staff"
            ? "bg-[#f1f1f1] text-gray-900"
            : "bg-[#e8e8e8] text-gray-800"
        }`}
      >
        <p>{m.body}</p>

        <span className="text-[11px] text-gray-400 block mt-1">
          {new Date(m.created_at).toLocaleString()}
        </span>
      </div>
    </div>
  ))
)}

            <div ref={bottomRef} />
          </div>

          {/* REPLY BAR */}
          <div className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-sm p-3 flex flex-col sm:flex-row gap-2">

            <input
               disabled={loading}
              type="text"
              placeholder={TEXT.replyPlaceholder}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              className={`flex-1 h-12 sm:h-10 px-3 text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-gray-200 ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            />

            <button
  onClick={sendReply}
  disabled={loading}
              className={`px-4 h-10 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-800 flex items-center justify-center gap-2 ${
    loading ? "opacity-50 cursor-not-allowed" : ""
  }`}
            >
              <FaPaperPlane /> {TEXT.send}
            </button>

            <button
              onClick={async () => {
                await adminUpdateMessageStatus(id, "closed");
                setTicket((t) => ({ ...t, status: "closed" }));
                navigate("/admin/messages");
              }}
              className="px-4 h-10 text-sm font-medium border border-red-200 text-red-600 rounded-md hover:bg-red-50 flex items-center justify-center gap-2"
            >
              <FaCheckCircle /> {TEXT.close}
            </button>

          </div>

        </main>
      </div>
    </div>
  );
}

export default AdminMessageView;